/*
 * Copyright (c) 2024-2025.
 * The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {ColumnFiltersState, getCoreRowModel, PaginationState, SortingState, useReactTable} from "@tanstack/react-table";
import {useDebouncedCallback} from "use-debounce";
import React, {useEffect, useState} from "react";

interface ChangeCustomFilter {
    filterName: string;
    data: any;
}

export interface useTableProps {
    defaultData?: any[];
    columns: any;
    defaultColumnFilters?: ColumnFiltersState;
    defaultGlobalFilter?: string;
    enableRowSelection?: boolean;
    defaultRecordsPerPage?: number;
    defaultRecordsPage?: number;
    defaultSorting?: SortingState;
}

export const useTable = (props: useTableProps) => {
    const [recordsTable, setRecordsTable] = useState<any[]>(props?.defaultData ? props.defaultData : []);
    const [rowSelection, setRowSelection] = React.useState({});
    const [paginationTotal, setPaginationTotal] = React.useState<number>(0);
    const [recordsCount, setRecordsCount] = React.useState<number>(props?.defaultData ? props.defaultData.length : 0);
    const [{pageIndex, pageSize}, setPagination] = useState<PaginationState>({
        pageIndex: props?.defaultRecordsPage ? props.defaultRecordsPage : 0,
        pageSize: props?.defaultRecordsPerPage ? props.defaultRecordsPerPage : 5,
    });
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(props.defaultColumnFilters ? props.defaultColumnFilters : []);
    const [globalFilter, setGlobalFilter] = React.useState(props?.defaultGlobalFilter ? props.defaultGlobalFilter : "");
    const [sorting, setSorting] = useState<SortingState>(props.defaultSorting ? props.defaultSorting : []);
    const [isLoading, setIsLoading] = useState(false);
    const [lastColumnFilters, setLastColumnFilters] = React.useState<ColumnFiltersState>([]);

    // remove nullable data from search query
    const clearNullableFilters = (items: ColumnFiltersState) => {
        const newItems = items.filter(item => {
            if (Array.isArray(item.value)) {
                const array = item.value;
                const empty = array.every(item => item.length === 0);
                if (empty) {
                    return false;
                }
            }

            return (
                (Array.isArray(item.value) && item.value.length > 0) ||
                (typeof item.value === "number" && true) ||
                (typeof item.value === "string" && true) ||
                (typeof item.value === "boolean" && item.value !== false)
            );
        });

        return newItems.length > 0 ? newItems : [];
    };

    // mass action - get reservation ids
    const getReservationIdsFromSelectedRows = () => {
        const selectedKeys = Object.keys(rowSelection).map(key => parseInt(key));
        return recordsTable
            .map((reservation, index) => {
                return selectedKeys.includes(index) ? reservation.id : null;
            })
            .filter(item => item !== null);
    };

    // selected value to filters
    const getSelectedValueFilter = (filterId: string, defaultValue?: string | number | null | []) => {
        const findFilter = columnFilters.find(item => item.id === filterId);
        const isFilledValue = findFilter && findFilter.value;
        return isFilledValue ? findFilter.value : defaultValue ? defaultValue : null;
    };

    // global search
    const onChangeSearchHandler = data => {
        setGlobalFilter(data);
    };

    // sorting handler
    const sortChangeHandler = useDebouncedCallback(headerId => {
        console.log("sortChangeHandler", headerId);
        const findIndex = sorting.findIndex(item => item.id === headerId);
        // if (findIndex === -1) {
        //     setSorting([{desc: true, id: headerId}]);
        // } else {
        //     if (sorting[findIndex].desc === true) {
        //         setSorting([{desc: false, id: headerId}]);
        //     } else {
        //         setSorting([]);
        //     }
        // }
        if (findIndex === -1) {
            setSorting(prev => {
                return [
                    ...prev,
                    {
                        desc: true,
                        id: headerId,
                    },
                ];
            });
        } else {
            if (sorting[findIndex].desc) {
                setSorting(prev => {
                    return [...prev.filter(item => item.id !== headerId), {desc: false, id: headerId}];
                });
            } else {
                setSorting(prev => {
                    return [...prev.filter(item => item.id !== headerId)];
                });
            }
        }
    }, 300);

    // merge one filter fo custom filters
    const onChangeFilterHandler = (filterName: string, data: any) => {
        const itemIndex = columnFilters.findIndex(item => item.id === filterName);
        if (itemIndex !== -1) {
            const newFilter = [...columnFilters];
            newFilter[itemIndex].value = data;
            setColumnFilters(newFilter);
        } else {
            setColumnFilters([...columnFilters, {id: filterName, value: data}]);
        }
    };

    // merge multiple filters to custom filters
    const changeFiltersHandler = (filtersData: ChangeCustomFilter[]) => {
        const tempColumnFilters = [...columnFilters];
        const newColumnFilters = filtersData.map(filter => {
            return {id: filter.filterName, value: filter.data};
        });
        const mergedFilters = newColumnFilters.concat(tempColumnFilters.filter(a => !newColumnFilters.find(b => b.id === a.id)));
        setColumnFilters([...mergedFilters]);
    };

    const removeColumnFilter = () => {};

    // pagination
    const pagination = React.useMemo(() => ({pageIndex, pageSize}), [pageIndex, pageSize]);

    // pagination handlers
    const paginationSetPageIndexHandler = useDebouncedCallback((page: number) => table.setPageIndex(page), 300);
    const paginationNextPageHandler = useDebouncedCallback(() => table.nextPage(), 300);
    const paginationPreviousPageHandler = useDebouncedCallback(() => table.previousPage(), 300);
    const paginationSetPageSize = useDebouncedCallback((pageSize: number) => table.setPageSize(pageSize), 300);

    // table def
    const table = useReactTable({
        data: recordsTable,
        columns: props.columns,
        pageCount: paginationTotal,
        state: {
            rowSelection: rowSelection,
            pagination: pagination,
            columnFilters: columnFilters,
            globalFilter: globalFilter,
            sorting: sorting,
        },
        enableRowSelection: props.enableRowSelection ? props.enableRowSelection : false,
        onRowSelectionChange: setRowSelection,
        enableGlobalFilter: true,
        enableColumnFilters: true,
        manualFiltering: true,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        filterFns: undefined,
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: true,
        onSortingChange: setSorting,
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
        debugTable: true,
    });

    useEffect(() => {
        console.log("sorting", sorting);
    }, [sorting]);

    return {
        table,
        isLoading,
        setIsLoading,
        recordsCount,
        setRecordsCount,
        lastColumnFilters,
        setLastColumnFilters,
        sortChangeHandler,
        clearNullableFilters,
        onChangeSearchHandler,
        getSelectedValueFilter,
        paginationSetPageIndexHandler,
        paginationNextPageHandler,
        paginationPreviousPageHandler,
        paginationSetPageSize,
        recordsTable,
        setRecordsTable,
        setPagination,
        paginationTotal,
        setPaginationTotal,
        columnFilters,
        setColumnFilters,
        globalFilter,
        setGlobalFilter,
        pageIndex,
        pageSize,
        sorting,
        setRowSelection,
        rowSelection,
        getReservationIdsFromSelectedRows,
        onChangeFilterHandler,
        removeColumnFilter,
        changeFiltersHandler,
    };
};
